/**
 * @file useIntelLayout.ts
 * @brief U4: load and save the Intel / Points tab layout (per user, server).
 *
 * @details
 * `GET /api/auth/intel-layout` once when Meeting Details opens; until it
 * answers, or when it fails, the default layout shows (never blocks the
 * screen). A change updates the screen first, then `PUT`s that tab ALONE
 * (the other tab is never overwritten by a stale copy). A failed save keeps
 * the layout on this device, says nothing and never retries: the next
 * successful save sends the whole tab again. Reset layout (the meeting's •••
 * menu, U5) = `PUT { intel: null, points: null }`, both tabs at once.
 * Contract: workspace docs/product/intel-layout-u4.md. Pure rules: ./intelLayout.ts.
 *
 * @author DES
 * @date 03-11-2026
 */

import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "@/shared/api/client";
import {
  applyOrder,
  buildTabSave,
  canMove,
  dropSection,
  moveSection,
  readIntelLayout,
  visibleUnits,
  type IntelLayout,
  type IntelLayoutTab,
  type MoveAction,
  type TabLayout,
} from "./intelLayout";
import type { SectionSort } from "./useSectionSort";

const INTEL_LAYOUT_URL = "/api/auth/intel-layout";
export const INTEL_LAYOUT_QUERY_KEY = ["auth", "intel-layout"] as const;

export interface IntelLayoutStore {
  layout: IntelLayout | null;
  /** Screen first, then the server; `null` = Reset layout for that tab. */
  saveTab: (tab: IntelLayoutTab, value: TabLayout | null) => void;
  /** U5 "Reset layout" (••• menu): both tabs back to today's layout. */
  resetAll: () => void;
}

/** @param enabled false (demo meeting) = default layout, changes stay on the screen. */
export function useIntelLayout(enabled: boolean): IntelLayoutStore {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: INTEL_LAYOUT_QUERY_KEY,
    queryFn: async () => readIntelLayout(await apiGet<unknown>(INTEL_LAYOUT_URL)),
    enabled,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const saveTab = useCallback(
    (tab: IntelLayoutTab, value: TabLayout | null) => {
      const prev = qc.getQueryData<IntelLayout | null>(INTEL_LAYOUT_QUERY_KEY) ?? null;
      const next: IntelLayout = { intel: prev?.intel ?? null, points: prev?.points ?? null, [tab]: value };
      qc.setQueryData(INTEL_LAYOUT_QUERY_KEY, next.intel || next.points ? next : null);
      if (!enabled) return;
      // Fire and forget: a failure keeps the local layout (contract).
      apiPut(INTEL_LAYOUT_URL, { [tab]: value }).catch(() => undefined);
    },
    [qc, enabled],
  );

  const resetAll = useCallback(() => {
    qc.setQueryData(INTEL_LAYOUT_QUERY_KEY, null);
    if (!enabled) return;
    apiPut(INTEL_LAYOUT_URL, { intel: null, points: null }).catch(() => undefined);
  }, [qc, enabled]);

  return { layout: query.data ?? null, saveTab, resetAll };
}

/** One tab's layout as the screen uses it. */
export interface TabLayoutControl {
  tab: IntelLayoutTab;
  /** This meeting's keys in the applied order (hidden ones included). */
  ordered: string[];
  /** The blocks drawn, in order (see intelLayout.ts visibleUnits). */
  units: string[][];
  /** Every block in order, hidden ones too (each still decides to draw nothing). */
  allUnits: string[][];
  isCollapsed: (key: string) => boolean;
  toggle: (key: string) => void;
  canMove: (key: string, action: MoveAction) => boolean;
  move: (key: string, action: MoveAction) => void;
  drop: (draggedKey: string, targetKey: string) => void;
  /** U5: the heading drag (useSectionSort.ts); absent = no drag. */
  sort?: SectionSort;
}

/**
 * @param present this meeting's keys in today's order, empty ones included
 * @param isVisible whether a key's section is drawn on this meeting
 * @param groups keys drawn as one block (web: the Attached notes card)
 */
export function useTabLayout(
  store: IntelLayoutStore,
  tab: IntelLayoutTab,
  present: readonly string[],
  isVisible: (key: string) => boolean,
  groups: readonly (readonly string[])[] = [],
): TabLayoutControl {
  const saved = store.layout?.[tab] ?? null;
  const { saveTab } = store;
  // Stable inputs by value: callers rebuild the arrays every render.
  const presentSig = present.join("\u0000");
  const visibleSig = present.map((k) => (isVisible(k) ? 1 : 0)).join("");
  const groupsSig = groups.map((g) => g.join("\u0000")).join("\u0001");

  return useMemo(() => {
    const ordered = applyOrder(present, saved?.order);
    const units = visibleUnits(ordered, isVisible, groups);
    const collapsed = new Set(saved?.collapsed ?? []);
    const save = (order: string[], shut: Set<string>) =>
      saveTab(tab, buildTabSave(tab, present, order, shut, saved));
    return {
      tab,
      ordered,
      units,
      allUnits: visibleUnits(ordered, () => true, groups),
      isCollapsed: (key) => collapsed.has(key),
      toggle: (key) => {
        const shut = new Set(collapsed);
        if (shut.has(key)) shut.delete(key);
        else shut.add(key);
        save(ordered, shut);
      },
      canMove: (key, action) => canMove(units, key, action),
      move: (key, action) => {
        if (canMove(units, key, action)) save(moveSection(ordered, units, key, action), collapsed);
      },
      drop: (draggedKey, targetKey) => {
        const next = dropSection(ordered, units, draggedKey, targetKey);
        if (next.some((k, i) => k !== ordered[i])) save(next, collapsed);
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the *Sig strings stand for present / isVisible / groups
  }, [saved, saveTab, tab, presentSig, visibleSig, groupsSig]);
}
